import type { Pead } from "epsm-api-types/models";
import type React from "react";
import { Watermark } from "@/shared/components/Watermark";
import { MAIN_CHART_HEIGHT, SMALL_CHART_HEIGHT } from "../../../consts";
import { filterDriftsDependingOnQtr } from "../../../lib/helpers/filterDriftsDependsOnQtr";
import { getClassForPeadChart } from "../../../lib/helpers/getClassForPeadChart/getClassForPeadChart";
import type { ChartData, PeadFilterType } from "../../../types";
import { DriftType } from "../../../types";
import { AllPeads } from "../AllPeads/AllPeads";
import { CurrentPeadData } from "../CurrentPeadData/CurrentPeadData";
import { DriftChart } from "../DriftChart/DriftChart";
import { PeadHistory } from "../PeadHistory/PeadHistory";

type Props = {
	relativeToSpy: boolean;
	widgetWidth: number;
	activeChart: Pead | null;
	ticker: string;
	stdDev?: boolean;
	priceLine: ChartData;
	currentPeadAccy: number | string;
	historyType?: string;
	peadFilter: PeadFilterType;
	showSeasonal: boolean;
	currentDrift: Pead | null;
	data: Pead[];
	qtr?: string;
	widgetId: string;
	setActiveChart: (pead: Pead | null) => void;
	setPeadStateWithWidgetId: () => void;
};

export const PeadSmallWidget: React.FC<Props> = ({
	data,
	relativeToSpy,
	activeChart,
	ticker,
	stdDev,
	priceLine,
	currentPeadAccy,
	peadFilter,
	showSeasonal,
	currentDrift,
	qtr = "20 Qtr",
	widgetId,
	setActiveChart,
	setPeadStateWithWidgetId,
}) => {
	const lastDriftData = data[0].earning_day;

	const isDriftStillCalculating =
		activeChart?.earning_day.getTime() === lastDriftData.getTime() &&
		!("_id" in activeChart);

	return (
		<div className="pead-small-widget" data-testid="pead-small-widget">
			<div className="pead-charts">
				<h1>PEAD in number of days from earnings</h1>

				<div className="relative">
					{activeChart?.is_finished && isDriftStillCalculating ? (
						<div
							data-testid="drift-calculated-msg"
							className="w-full h-[400px] relative"
						>
							<h2 className="text-gold absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
								The drift is being calculated
							</h2>
						</div>
					) : (
						<>
							<DriftChart
								relativeToSpy={relativeToSpy}
								classNames={"col-span-2"}
								pead={activeChart}
								type={DriftType.current}
								ticker={ticker}
								isMainChart
								stdDev={stdDev}
								priceLine={priceLine}
								currentPeadAccy={currentPeadAccy}
								style={{ height: MAIN_CHART_HEIGHT }}
							/>

							<Watermark className="absolute right-[10px] bottom-[20px] p-2" />
						</>
					)}
				</div>

				<div>
					<h1>Drift Categories</h1>

					<div className="drift-row mb-1 mt-1 gap-1">
						<DriftChart
							relativeToSpy={relativeToSpy}
							type={DriftType.A}
							ticker={ticker}
							stdDev={stdDev}
							priceLine={priceLine}
							currentPeadAccy={currentPeadAccy}
							style={{ height: SMALL_CHART_HEIGHT }}
							pead={
								(showSeasonal
									? currentDrift?.quarter_1
									: currentDrift?.type_a) || currentDrift
							}
							classNames={`${getClassForPeadChart(
								showSeasonal,
								DriftType.A,
								DriftType.Q1,
								activeChart?.drift_unit.value as DriftType,
							)}`}
							onClick={() => {
								setActiveChart(
									(showSeasonal
										? currentDrift?.quarter_1
										: currentDrift?.type_a) || currentDrift,
								);
							}}
						/>

						<DriftChart
							relativeToSpy={relativeToSpy}
							type={DriftType.B}
							ticker={ticker}
							stdDev={stdDev}
							priceLine={priceLine}
							currentPeadAccy={currentPeadAccy}
							style={{ height: SMALL_CHART_HEIGHT }}
							pead={
								(showSeasonal
									? currentDrift?.quarter_2
									: currentDrift?.type_b) || currentDrift
							}
							classNames={`${getClassForPeadChart(
								showSeasonal,
								DriftType.B,
								DriftType.Q2,
								activeChart?.drift_unit.value as DriftType,
							)} `}
							onClick={() => {
								setActiveChart(
									(showSeasonal
										? currentDrift?.quarter_2
										: currentDrift?.type_b) || currentDrift,
								);
							}}
						/>
					</div>

					<div className="drift-row gap-1">
						<DriftChart
							relativeToSpy={relativeToSpy}
							type={DriftType.C}
							ticker={ticker}
							stdDev={stdDev}
							priceLine={priceLine}
							currentPeadAccy={currentPeadAccy}
							style={{ height: SMALL_CHART_HEIGHT }}
							pead={
								(showSeasonal
									? currentDrift?.quarter_3
									: currentDrift?.type_c) || currentDrift
							}
							classNames={`${getClassForPeadChart(
								showSeasonal,
								DriftType.C,
								DriftType.Q3,
								activeChart?.drift_unit.value as DriftType,
							)}`}
							onClick={() => {
								setActiveChart(
									(showSeasonal
										? currentDrift?.quarter_3
										: currentDrift?.type_c) || currentDrift,
								);
							}}
						/>

						<DriftChart
							relativeToSpy={relativeToSpy}
							type={DriftType.D}
							ticker={ticker}
							stdDev={stdDev}
							priceLine={priceLine}
							currentPeadAccy={currentPeadAccy}
							style={{ height: SMALL_CHART_HEIGHT }}
							pead={
								(showSeasonal
									? currentDrift?.quarter_4
									: currentDrift?.type_d) || currentDrift
							}
							classNames={`${getClassForPeadChart(
								showSeasonal,
								DriftType.D,
								DriftType.Q4,
								activeChart?.drift_unit.value as DriftType,
							)}`}
							onClick={() => {
								setActiveChart(
									(showSeasonal
										? currentDrift?.quarter_4
										: currentDrift?.type_d) || currentDrift,
								);
							}}
						/>
					</div>

					<div className="col-span-2">
						<CurrentPeadData
							activePead={currentDrift}
							relativeToSpy={relativeToSpy}
							setPeadState={setPeadStateWithWidgetId}
						/>
					</div>
				</div>
			</div>

			<div className="flex flex-col w-full max-h-[100vh]">
				<div className="p-1 pt-0 pb-0 gap-3">
					<PeadHistory
						ticker={ticker}
						data={filterDriftsDependingOnQtr(
							data,
							qtr.startsWith("20"),
							peadFilter,
						)}
						setActiveChart={setActiveChart}
						peadFilter={peadFilter}
					/>
				</div>

				{peadFilter === "PEAD" && (
					<div className="flex flex-col m-1">
						<h1 className="">
							All quarters normalized by number of days post earnings release
						</h1>

						<AllPeads
							data={data}
							widgetId={widgetId}
							ticker={ticker}
							peadFilter={peadFilter}
							qtr={qtr}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
